import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopBackLink from '../../components/BopBackLink';
import Modal from '../../components/Modal';
import ApiInspector from '../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import { apiFetch } from '../../lib/api';
import { usePermesso } from '../../context/PermessiContext';
import { formatQuantity } from '../../utils/formatters';
import { useBloccoRecordContesto } from '../../hooks/useBloccoRecordContesto';
import { API_DOCS } from './InventarioFisicoDettaglioPage.api';

const MODULO = 'magazzino.inventari_fisici';
type StatoInventario = 'BOZZA' | 'CONFERMATO';

const DEP_TAG: Record<string, string> = { FISSO: 'FISSO', CONTO_TERZI: 'CT', COMMESSA: 'COM' };
function depositoLabel(d: { nome: string; tipo: string } | null | undefined): string {
  if (!d) return '';
  let nome = d.nome;
  if (d.tipo === 'COMMESSA' && nome.startsWith('Commessa: ')) nome = nome.slice('Commessa: '.length);
  if (nome.length > 40) nome = nome.slice(0, 39).trimEnd() + '…';
  const tag = DEP_TAG[d.tipo] || d.tipo;
  return `${nome} [${tag}]`;
}

interface Testata {
  id: number;
  societaId: number;
  numero: number | null;
  anno: number | null;
  data: string;
  descrizione: string;
  stato: StatoInventario;
  note: string | null;
  deposito: { id: number; nome: string; tipo: string } | null;
  movimentoRettifica: { id: number; numero: number | null; anno: number | null; stato: string } | null;
}

interface Riga {
  id: number;
  ordine: number;
  articoloId: number;
  articolo: { id: number; codice: string; descrizioneBase: string } | null;
  quantitaTeorica: number | string;
  quantitaRilevata: number | string | null;
  differenza: number | string | null;
  note: string | null;
}

interface LogEntry {
  id: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface ArticoloLookup {
  id: number;
  codice: string;
  descrizioneBase: string;
  tipologia: string;
}

export default function InventarioFisicoDettaglioPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);

  const [testata, setTestata] = useState<Testata | null>(null);
  const [righe, setRighe] = useState<Riga[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [working, setWorking] = useState(false);

  useBloccoRecordContesto({
    recordSocietaId: testata?.societaId,
    label: testata && testata.numero != null ? `Inventario ${testata.numero}/${testata.anno}` : 'Inventario fisico',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // @api GET /api/inventari-fisici/:id -> { success: true, data: Testata + righe, logs: [...] }
      const res = await apiFetch<{ success: boolean; data: Testata & { righe: Riga[] }; logs: LogEntry[] }>(`/api/inventari-fisici/${id}`);
      const { righe: rg, ...rest } = res.data;
      setTestata(rest as Testata);
      setRighe(rg ?? []);
      setLogs(res.logs ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento dell\'inventario');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (Number.isFinite(id)) load(); }, [id]);

  const isBozza      = testata?.stato === 'BOZZA';
  const isConfermato = testata?.stato === 'CONFERMATO';

  // Statistiche righe
  const stats = useMemo(() => {
    const totale = righe.length;
    const rilevate = righe.filter(r => r.quantitaRilevata !== null && r.quantitaRilevata !== undefined).length;
    const conDifferenza = righe.filter(r => {
      const d = r.differenza;
      return d !== null && d !== undefined && Number(d) !== 0;
    }).length;
    return { totale, rilevate, mancanti: totale - rilevate, conDifferenza };
  }, [righe]);

  // ───────── PUT quantità rilevata inline ─────────
  // Manteniamo un draft locale per ogni riga; salvataggio su blur.
  const [rilevateDraft, setRilevateDraft] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const draftValue = (r: Riga): string => {
    if (r.id in rilevateDraft) return rilevateDraft[r.id];
    return r.quantitaRilevata === null || r.quantitaRilevata === undefined ? '' : String(r.quantitaRilevata);
  };

  const onChangeRilevata = (rigaId: number, v: string) => {
    setRilevateDraft(d => ({ ...d, [rigaId]: v }));
  };

  const onBlurRilevata = async (riga: Riga) => {
    const draft = rilevateDraft[riga.id];
    if (draft === undefined) return; // niente da salvare
    const current = riga.quantitaRilevata === null || riga.quantitaRilevata === undefined ? '' : String(riga.quantitaRilevata);
    if (draft === current) {
      setRilevateDraft(d => { const x = { ...d }; delete x[riga.id]; return x; });
      return;
    }

    const payload: any = {};
    if (draft === '' || draft === null) {
      payload.quantitaRilevata = null;
    } else {
      const q = Number(draft);
      if (!Number.isFinite(q) || q < 0) {
        setActionError(`Riga ${riga.ordine}: quantità rilevata non valida`);
        return;
      }
      payload.quantitaRilevata = q;
    }

    setSavingId(riga.id);
    setActionError('');
    try {
      // @api PUT /api/inventari-fisici/:id/righe/:rigaId { quantitaRilevata } -> { success: true }
      await apiFetch(`/api/inventari-fisici/${id}/righe/${riga.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      // Aggiorno la riga in locale (qta + differenza)
      const teor = Number(riga.quantitaTeorica);
      const newRilevata: number | null = payload.quantitaRilevata;
      const newDiff = newRilevata === null ? null : newRilevata - teor;
      setRighe(rs => rs.map(r => r.id === riga.id
        ? { ...r, quantitaRilevata: newRilevata as any, differenza: newDiff as any }
        : r));
      setRilevateDraft(d => { const x = { ...d }; delete x[riga.id]; return x; });
    } catch (e: any) {
      setActionError(e.message || 'Errore nel salvataggio della riga');
    } finally {
      setSavingId(null);
    }
  };

  // ───────── Modale "Modifica testata" ─────────
  const [modalTestata, setModalTestata] = useState<null | { data: string; descrizione: string; note: string }>(null);
  const [testataError, setTestataError] = useState('');
  const apriModificaTestata = () => {
    if (!testata) return;
    setModalTestata({ data: testata.data.slice(0, 10), descrizione: testata.descrizione, note: testata.note ?? '' });
    setTestataError('');
  };
  const chiudiModificaTestata = () => { setModalTestata(null); setTestataError(''); };
  const salvaTestata = async () => {
    if (!modalTestata) return;
    if (!modalTestata.data) { setTestataError('Data obbligatoria'); return; }
    if (!modalTestata.descrizione.trim()) { setTestataError('Descrizione obbligatoria'); return; }
    setWorking(true); setTestataError('');
    try {
      // @api PUT /api/inventari-fisici/:id { data, descrizione, note } -> { success: true, data: { id } }
      await apiFetch(`/api/inventari-fisici/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          data: modalTestata.data,
          descrizione: modalTestata.descrizione.trim(),
          note: modalTestata.note.trim() || null,
        }),
      });
      chiudiModificaTestata();
      load();
    } catch (e: any) {
      setTestataError(e.message || 'Errore nel salvataggio');
    } finally {
      setWorking(false);
    }
  };

  // ───────── Modale "Aggiungi articolo manualmente" ─────────
  const [modalAdd, setModalAdd] = useState<null | { articolo: ArticoloLookup | null; quantitaRilevata: string; note: string }>(null);
  const [addError, setAddError] = useState('');
  const [searchArt, setSearchArt] = useState('');
  const [opzioni, setOpzioni] = useState<ArticoloLookup[]>([]);
  const [optLoading, setOptLoading] = useState(false);

  const apriAddRiga = () => { setModalAdd({ articolo: null, quantitaRilevata: '', note: '' }); setAddError(''); setSearchArt(''); setOpzioni([]); };
  const chiudiAddRiga = () => { setModalAdd(null); setAddError(''); };

  useEffect(() => {
    if (!modalAdd || modalAdd.articolo) return;
    if (searchArt.trim().length < 1) { setOpzioni([]); return; }
    const handle = setTimeout(async () => {
      if (!testata) return;
      setOptLoading(true);
      try {
        const qs = new URLSearchParams({
          societaId: String(testata.societaId),
          q: searchArt.trim(),
          inventarioId: String(id),
        });
        // @api GET /api/inventari-fisici/lookup/articoli?societaId=&q=&inventarioId= -> { success: true, data: [{ id, codice, descrizioneBase, tipologia }] }
        const res = await apiFetch<{ success: boolean; data: ArticoloLookup[] }>(`/api/inventari-fisici/lookup/articoli?${qs.toString()}`);
        setOpzioni(res.data ?? []);
      } catch {
        setOpzioni([]);
      } finally {
        setOptLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchArt, modalAdd?.articolo]);

  const salvaAddRiga = async () => {
    if (!modalAdd) return;
    if (!modalAdd.articolo) { setAddError('Seleziona un articolo'); return; }
    const q = modalAdd.quantitaRilevata.trim();
    let qNum: number | null = null;
    if (q !== '') {
      const n = Number(q);
      if (!Number.isFinite(n) || n < 0) { setAddError('Quantità non valida'); return; }
      qNum = n;
    }
    setWorking(true); setAddError('');
    try {
      // @api POST /api/inventari-fisici/:id/righe { articoloId, quantitaRilevata, note } -> { success: true, data: { id, ordine } }
      await apiFetch(`/api/inventari-fisici/${id}/righe`, {
        method: 'POST',
        body: JSON.stringify({
          articoloId: modalAdd.articolo.id,
          quantitaRilevata: qNum,
          note: modalAdd.note.trim() || null,
        }),
      });
      chiudiAddRiga();
      load();
    } catch (e: any) {
      setAddError(e.message || 'Errore nell\'aggiunta');
    } finally {
      setWorking(false);
    }
  };

  const eliminaRiga = async (r: Riga) => {
    if (!window.confirm(`Eliminare la riga ${r.ordine} (${r.articolo?.codice})?`)) return;
    setWorking(true); setActionError('');
    try {
      // @api DELETE /api/inventari-fisici/:id/righe/:rigaId -> { success: true }
      await apiFetch(`/api/inventari-fisici/${id}/righe/${r.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) {
      setActionError(e.message || 'Errore nell\'eliminazione');
    } finally {
      setWorking(false);
    }
  };

  // ───────── Conferma ─────────
  const conferma = async () => {
    if (!testata) return;
    if (stats.mancanti > 0) {
      setActionError(`Mancano ancora ${stats.mancanti} righe da rilevare. Compila la quantità rilevata su tutti gli articoli prima di confermare.`);
      return;
    }
    if (!window.confirm(`Confermare l'inventario?\n\nVerrà generato automaticamente un movimento di rettifica per allineare le giacenze${stats.conDifferenza > 0 ? ` (${stats.conDifferenza} righe con differenza)` : ''}. Operazione irreversibile.`)) return;
    setWorking(true); setActionError('');
    try {
      // @api POST /api/inventari-fisici/:id/conferma -> { success: true, data: { id, numero, anno, movimento, righeApplicate } }
      await apiFetch(`/api/inventari-fisici/${id}/conferma`, { method: 'POST' });
      load();
    } catch (e: any) {
      setActionError(e.message || 'Errore nella conferma');
    } finally {
      setWorking(false);
    }
  };

  const elimina = async () => {
    if (!testata || !isBozza) return;
    if (!window.confirm('Eliminare questa bozza di inventario?\n\nLe righe congelate verranno scartate; le giacenze non sono toccate.')) return;
    setWorking(true); setActionError('');
    try {
      // @api DELETE /api/inventari-fisici/:id -> { success: true }
      await apiFetch(`/api/inventari-fisici/${id}`, { method: 'DELETE' });
      navigate('/magazzino/inventari-fisici');
    } catch (e: any) {
      setActionError(e.message || 'Errore nell\'eliminazione');
      setWorking(false);
    }
  };

  // ───────── Render ─────────

  if (loading)  return <Layout><div className="bop-empty-state">Caricamento…</div></Layout>;
  if (error)    return <Layout><div className="bop-inline-error">{error}</div></Layout>;
  if (!testata) return <Layout><div className="bop-empty-state">Inventario non trovato</div></Layout>;

  const titolo = testata.numero != null
    ? `Inventario ${testata.numero}/${testata.anno}`
    : `Bozza inventario #${testata.id}`;

  const statoBadge = (
    <span className={`bop-badge ${isConfermato ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
      {testata.stato.charAt(0) + testata.stato.slice(1).toLowerCase()}
    </span>
  );

  return (
    <Layout>
      <BopBackLink to="/magazzino/inventari-fisici" label="Torna agli inventari" />
      <div className="bop-page-header">
        <div>
          <h2 className="bop-page-title">{titolo} &nbsp; {statoBadge}</h2>
          <p className="bop-text-muted">
            {depositoLabel(testata.deposito)} · {new Date(testata.data).toLocaleDateString('it-IT')} — {testata.descrizione}
          </p>
        </div>
        <div className="bop-flex">
          {isBozza && canEdit && (
            <>
              <button className="bop-btn-secondary" onClick={apriModificaTestata} disabled={working}>Modifica testata</button>
              <button className="bop-btn-primary" onClick={conferma} disabled={working || stats.mancanti > 0}>Conferma</button>
            </>
          )}
          {isBozza && canDelete && (
            <button className="bop-btn-danger" onClick={elimina} disabled={working}>Elimina bozza</button>
          )}
        </div>
      </div>

      {actionError && <div className="bop-inline-error">{actionError}</div>}

      {/* Banner per inventari confermati: link al movimento */}
      {isConfermato && testata.movimentoRettifica && (
        <div className="bop-inline-error">
          Inventario CONFERMATO. <button className="bop-btn-secondary" onClick={() => navigate(`/magazzino/movimenti/${testata.movimentoRettifica!.id}`)}>
            Vai al movimento di rettifica {testata.movimentoRettifica.numero}/{testata.movimentoRettifica.anno}
          </button>
        </div>
      )}
      {isConfermato && !testata.movimentoRettifica && (
        <div className="bop-inline-error">
          Inventario CONFERMATO senza differenze: nessun movimento di rettifica generato.
        </div>
      )}

      {/* Statistiche rilevazione */}
      {isBozza && (
        <div className="bop-card">
          <div className="bop-flex">
            <div className="bop-form-group">
              <label className="bop-label">Righe totali</label>
              <div><strong>{stats.totale}</strong></div>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Rilevate</label>
              <div><strong>{stats.rilevate}</strong></div>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Mancanti</label>
              <div className={stats.mancanti > 0 ? 'bop-text-danger' : ''}><strong>{stats.mancanti}</strong></div>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Con differenza</label>
              <div><strong>{stats.conDifferenza}</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* Tabella righe */}
      <div className="bop-card">
        <div className="bop-flex-between">
          <h3 className="bop-mt-0">Articoli da rilevare</h3>
          {isBozza && canEdit && (
            <button className="bop-btn-secondary" onClick={apriAddRiga} disabled={working}>+ Aggiungi articolo</button>
          )}
        </div>

        {righe.length === 0 ? (
          <div className="bop-empty-state">Nessuna riga.</div>
        ) : (
          <table className="bop-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Articolo</th>
                <th className="bop-text-right">Qta teorica</th>
                <th className="bop-text-right">Qta rilevata</th>
                <th className="bop-text-right">Differenza</th>
                <th>Note</th>
                {isBozza && canEdit && <th className="bop-text-right">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {righe.map(r => {
                const teor = Number(r.quantitaTeorica);
                const ril  = r.quantitaRilevata === null || r.quantitaRilevata === undefined ? null : Number(r.quantitaRilevata);
                const diff = r.differenza === null || r.differenza === undefined ? null : Number(r.differenza);
                const isManual = teor === 0;
                return (
                  <tr key={r.id}>
                    <td>{r.ordine}</td>
                    <td>
                      {r.articolo ? (
                        <>
                          <strong>{r.articolo.codice}</strong>
                          {isManual && <span className="bop-badge bop-badge--info">&nbsp;manuale</span>}
                          <div className="bop-text-muted">{r.articolo.descrizioneBase}</div>
                        </>
                      ) : <span className="bop-text-muted">—</span>}
                    </td>
                    <td className="bop-text-right">{formatQuantity(teor)}</td>
                    <td className="bop-text-right">
                      {isBozza && canEdit ? (
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          className="bop-input bop-text-right"
                          value={draftValue(r)}
                          onChange={e => onChangeRilevata(r.id, e.target.value)}
                          onBlur={() => onBlurRilevata(r)}
                          disabled={savingId === r.id}
                          placeholder="da rilevare"
                        />
                      ) : ril === null
                          ? <span className="bop-text-muted">—</span>
                          : formatQuantity(ril)}
                    </td>
                    <td className="bop-text-right">
                      {diff === null
                        ? <span className="bop-text-muted">—</span>
                        : (
                            <span className={diff === 0 ? 'bop-text-muted' : (diff > 0 ? 'bop-badge bop-badge--success' : 'bop-badge bop-badge--danger')}>
                              {diff > 0 ? '+' : ''}{formatQuantity(diff)}
                            </span>
                          )}
                    </td>
                    <td><span className="bop-text-muted">{r.note || ''}</span></td>
                    {isBozza && canEdit && (
                      <td className="bop-text-right">
                        <BopRowActions actions={[
                          { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => eliminaRiga(r), variant: 'danger' },
                        ]} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Log attività */}
      <div className="bop-card">
        <h3 className="bop-mt-0">Attività</h3>
        {logs.length === 0 ? (
          <div className="bop-empty-state">Nessuna attività registrata.</div>
        ) : (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Utente</th>
                <th>Azione</th>
                <th>Dettaglio</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id}>
                  <td>{new Date(l.createdAt).toLocaleString('it-IT')}</td>
                  <td>{l.utenteName}</td>
                  <td>{l.azione}</td>
                  <td><span className="bop-text-muted">{l.dettaglio || ''}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* MODALI */}

      {modalTestata && (
        <Modal
          title="Modifica testata inventario"
          onClose={chiudiModificaTestata}
          footer={
            <>
              <button className="bop-btn-secondary" onClick={chiudiModificaTestata}>Annulla</button>
              <button className="bop-btn-primary" onClick={salvaTestata} disabled={working}>{working ? 'Salvo…' : 'Salva'}</button>
            </>
          }
        >
          {testataError && <div className="bop-inline-error">{testataError}</div>}
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Data *</label>
              <input type="date" className="bop-input"
                value={modalTestata.data}
                onChange={e => setModalTestata(m => m && { ...m, data: e.target.value })} />
            </div>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Descrizione *</label>
            <input className="bop-input"
              value={modalTestata.descrizione}
              onChange={e => setModalTestata(m => m && { ...m, descrizione: e.target.value })} />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea className="bop-input" rows={2}
              value={modalTestata.note}
              onChange={e => setModalTestata(m => m && { ...m, note: e.target.value })} />
          </div>
        </Modal>
      )}

      {modalAdd && (
        <Modal
          title="Aggiungi articolo (non in giacenza)"
          onClose={chiudiAddRiga}
          footer={
            <>
              <button className="bop-btn-secondary" onClick={chiudiAddRiga}>Annulla</button>
              <button className="bop-btn-primary" onClick={salvaAddRiga} disabled={working || !modalAdd.articolo}>{working ? 'Aggiungo…' : 'Aggiungi'}</button>
            </>
          }
        >
          {addError && <div className="bop-inline-error">{addError}</div>}
          <p className="bop-text-muted">
            Aggiungi un articolo trovato fisicamente ma non presente in giacenza.
            La quantità teorica sarà 0; alla conferma genererà una rettifica positiva.
          </p>

          <div className="bop-form-group">
            <label className="bop-label">Articolo *</label>
            {modalAdd.articolo ? (
              <div className="bop-flex-between">
                <div>
                  <strong>{modalAdd.articolo.codice}</strong> — {modalAdd.articolo.descrizioneBase}
                  <div className="bop-text-muted">{modalAdd.articolo.tipologia}</div>
                </div>
                <button className="bop-btn-secondary" onClick={() => { setModalAdd(m => m && { ...m, articolo: null }); setSearchArt(''); }}>
                  Cambia
                </button>
              </div>
            ) : (
              <>
                <input
                  className="bop-input"
                  placeholder="Cerca articolo per codice o descrizione…"
                  value={searchArt}
                  onChange={e => setSearchArt(e.target.value)}
                  autoFocus
                />
                {opzioni.length > 0 && (
                  <div className="bop-card">
                    {opzioni.map(a => (
                      <div key={a.id} className="bop-table--clickable" role="button"
                        onClick={() => setModalAdd(m => m && { ...m, articolo: a })}
                        style={{ padding: '6px 8px', borderBottom: '1px solid var(--bop-color-border)', cursor: 'pointer' }}>
                        <strong>{a.codice}</strong> — {a.descrizioneBase}
                        <span className="bop-text-muted">&nbsp;· {a.tipologia}</span>
                      </div>
                    ))}
                  </div>
                )}
                {optLoading && <div className="bop-text-muted">Cerco…</div>}
              </>
            )}
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Quantità rilevata</label>
              <input
                type="number"
                step="0.001"
                min="0"
                className="bop-input"
                value={modalAdd.quantitaRilevata}
                onChange={e => setModalAdd(m => m && { ...m, quantitaRilevata: e.target.value })}
                placeholder="puoi compilarla anche dopo"
              />
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <input className="bop-input"
              value={modalAdd.note}
              onChange={e => setModalAdd(m => m && { ...m, note: e.target.value })}
              placeholder="opzionale" />
          </div>
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
